import { Fragment, useMemo, useState } from "react";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { FilterBar, matches } from "../../components/FilterBar";
import { QuoteEditor } from "../../components/QuoteEditor";
import type { Company } from "../../lib/types";

interface Quote {
    id: string;
    reference: string | null;
    company_id: string | null;
    contact_id: string | null;
    inquiry_id: string | null;
    status: "draft" | "sent" | "approved" | "rejected" | "invoiced" | "paid";
    gst_rate: string;
    apply_gst: boolean;
    payment_terms: string | null;
    valid_until: string | null;
    notes: string | null;
    created_at: string;
}

interface QuoteLine {
    id: string;
    quote_id: string;
    service: string;
    hours: string;
    rate: string;
    line_total: string;
    sort_order: number;
    gst_free: boolean | null;
}

const STATUS = ["draft", "sent", "approved", "rejected", "invoiced", "paid"]
    .map((s) => ({ value: s, label: titleCase(s) }));

const statusChip = (s: Quote["status"]) =>
    s === "approved" || s === "paid" ? "pass" : s === "rejected" ? "block" : s === "draft" ? "mute" : "info";

const currency = (n: number): string =>
    n.toLocaleString("en-AU", { style: "currency", currency: "AUD" });

export const Quotes = () => {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "manager";

    const quotes = useResource<Quote[]>("/quotes");
    const lines = useResource<QuoteLine[]>("/quote-lines");
    const companies = useResource<Company[]>("/companies");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [expanded, setExpanded] = useState<string | null>(null);
    const [dialog, setDialog] = useState<
        { kind: "create" } | { kind: "edit"; quote: Quote } | null
    >(null);

    const refresh = () => { quotes.reload(); lines.reload(); setDialog(null); };

    const companyName = useMemo(() => {
        const map = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
        return (id: string | null) => (id ? map.get(id) ?? "—" : "—");
    }, [companies.data]);

    const linesFor = (quoteId: string) =>
        (lines.data ?? []).filter((l) => l.quote_id === quoteId).sort((a, b) => a.sort_order - b.sort_order);

    /** Totals derive from the lines — the same way the database derives line_total.
     *  GST applies only to lines that aren't GST-free, matching the editor. */
    const totalsFor = (quote: Quote) => {
        const qLines = linesFor(quote.id);
        const subtotal = qLines.reduce((sum, l) => sum + Number(l.line_total), 0);
        const taxable = qLines.reduce((sum, l) => sum + (l.gst_free ? 0 : Number(l.line_total)), 0);
        const gst = quote.apply_gst ? taxable * Number(quote.gst_rate) : 0;
        return { subtotal, gst, total: subtotal + gst };
    };

    const all = quotes.data ?? [];
    const filtered = all.filter((q) =>
        matches(search, q.reference, companyName(q.company_id)) &&
        (!filters.status || q.status === filters.status)
    );

    if (quotes.error) return <div className="error-banner">{quotes.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <QuoteEditor quote={null} onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <QuoteEditor quote={dialog.quote} onClose={() => setDialog(null)} onSaved={refresh} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search quotes…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[{ name: "status", allLabel: "All Status", options: STATUS }]}
                action={canEdit && <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ New Quote</button>}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Reference</th><th>Company</th><th>Valid until</th>
                                    <th>Total (inc GST)</th><th>Status</th>
                                    {canEdit && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((quote) => {
                                    const totals = totalsFor(quote);
                                    const quoteLines = linesFor(quote.id);
                                    return (
                                        <Fragment key={quote.id}>
                                            <tr className="clickable"
                                                onClick={() => setExpanded(expanded === quote.id ? null : quote.id)}>
                                                <td className="mono" style={{ fontWeight: 600 }}>{quote.reference}</td>
                                                <td>{companyName(quote.company_id)}</td>
                                                <td className="mono">{formatDate(quote.valid_until)}</td>
                                                <td className="mono">{currency(totals.total)}</td>
                                                <td><span className={`chip ${statusChip(quote.status)}`}>{titleCase(quote.status)}</span></td>
                                                {canEdit && (
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div className="row-actions">
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "edit", quote })}>Edit</button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                            {expanded === quote.id && (
                                                <tr>
                                                    <td colSpan={canEdit ? 6 : 5} style={{ background: "var(--surface-sunk)" }}>
                                                        <table>
                                                            <thead>
                                                                <tr><th>Service</th><th>Hours</th><th>Rate</th><th style={{ textAlign: "right" }}>Line total</th></tr>
                                                            </thead>
                                                            <tbody>
                                                                {quoteLines.map((line) => (
                                                                    <tr key={line.id}>
                                                                        <td>{line.service}</td>
                                                                        <td className="mono">{Number(line.hours)}</td>
                                                                        <td className="mono">{currency(Number(line.rate))}</td>
                                                                        <td className="mono" style={{ textAlign: "right" }}>{currency(Number(line.line_total))}</td>
                                                                    </tr>
                                                                ))}
                                                                <tr>
                                                                    <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>Subtotal</td>
                                                                    <td className="mono" style={{ textAlign: "right" }}>{currency(totals.subtotal)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan={3} style={{ textAlign: "right" }}>{quote.apply_gst ? `GST (${(Number(quote.gst_rate) * 100).toFixed(0)}%)` : "GST-free"}</td>
                                                                    <td className="mono" style={{ textAlign: "right" }}>{currency(totals.gst)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>Total</td>
                                                                    <td className="mono" style={{ textAlign: "right", fontWeight: 600 }}>{currency(totals.total)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        {quote.notes && (
                                                            <p style={{ fontSize: "0.8rem", color: "var(--muted)", marginTop: "0.5rem" }}>{quote.notes}</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canEdit ? 6 : 5} className="empty">No quotes match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
